import cx from 'classnames'
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import { useClickAway } from 'react-use'
import { TickerSuggestion } from '../../store/admin/tickerRating/selectors'
import listStyles from '../DropdownList/DropdownList.module.scss'
import SearchInput from '../SearchInput/SearchInput'
import styles from './TickerSearch.module.scss'

export type { TickerSuggestion }

interface Props {
  value: string
  onChange: (value: string) => void
  onClear: () => void
  onSelect: (ticker: string) => void
  suggestions: TickerSuggestion[]
  placeholder?: string
  testId?: string
}

const TickerSearch = ({
  value,
  onChange,
  onClear,
  onSelect,
  suggestions,
  placeholder = 'Search ticker',
  testId = 'ticker-search'
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const filtered = useMemo(() => {
    if (!value) return suggestions
    const upper = value.toUpperCase()
    return suggestions.filter((s) => s.ticker.toUpperCase().startsWith(upper))
  }, [suggestions, value])

  useClickAway(containerRef, () => setIsOpen(false))

  const handleFocus = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
      setIsOpen(true)
      setActiveIndex(-1)
    },
    [onChange]
  )

  const handleSelect = useCallback(
    (ticker: string) => {
      onSelect(ticker)
      setIsOpen(false)
      setActiveIndex(-1)
    },
    [onSelect]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || filtered.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((prev) => Math.min(prev + 1, filtered.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter': {
          /* activeIndex >= 0 → select highlighted; otherwise select top match */
          const pick = activeIndex >= 0 ? activeIndex : 0
          e.preventDefault()
          handleSelect(filtered[pick].ticker)
          break
        }
        case 'Escape':
          setIsOpen(false)
          setActiveIndex(-1)
          break
      }
    },
    [isOpen, filtered, activeIndex, handleSelect]
  )

  const showDropdown = isOpen && filtered.length > 0

  return (
    <div ref={containerRef} data-testid={testId} className={styles.container}>
      <SearchInput
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onClear={onClear}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          showDropdown && activeIndex >= 0
            ? `${listboxId}-option-${filtered[activeIndex].ticker}`
            : undefined
        }
      />
      {showDropdown && (
        <div id={listboxId} role="listbox" className={styles.dropdown}>
          <div className={listStyles.dropdownMenu}>
            {filtered.map((s, i) => (
              <div
                key={s.ticker}
                id={`${listboxId}-option-${s.ticker}`}
                role="option"
                aria-selected={i === activeIndex}
                className={cx(listStyles.dropdownRaw, listStyles.clickable, {
                  [styles.active]: i === activeIndex
                })}
                onClick={() => handleSelect(s.ticker)}
              >
                <span className={styles.ticker}>{s.ticker}</span>
                <span className={styles.count}> ({s.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TickerSearch
